import React, { useState } from "react";
import styled, { keyframes, css } from "styled-components";
import TribunalSection from "./CaseTeam";
import LifecyclePopoverModal from "./LifecyclePopoverModal";
import AgentDetailsModal from "./AgentModal";
import theme from "../../../../assets/theme";
import { HearingLink, NextHearing, ScratchBtn, UpdateTime } from "./CaseHeader";
import { Calendar } from "../HearingBundles/components/Icons";
import CaseStatusHistoryCard from "./CaseStatusHistoryCard";
import _ from "lodash";

const getInitials = (name) => {
  if (!name) return "NA";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

const AVATAR_COLORS = ["#2c3e50", "#8e44ad", "#e74c3c", "#f39c12"];

const EASE_OUT_EXPO = "cubic-bezier(0.16, 1, 0.3, 1)";
const EASE_SPRING = "cubic-bezier(0.34, 1.56, 0.64, 1)";

const motionSafe = (animationCss) => css`
  ${animationCss}
  @media (prefers-reduced-motion: reduce) {
    animation: none !important;
    transition: none !important;
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(14px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const growIn = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`;

const scaleFadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.96);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const PartyContent = ({
  label,
  badgeBg,
  badgeColor,
  party,
  partyRole,
  onViewAgents,
  respondentParties,
}) => {
  let actualParty = null;
  let agents = [];

  if (!party) {
    actualParty = null;
  } else if (Array.isArray(party)) {
    actualParty = party[0];
  } else if (party?.party) {
    actualParty = party.party;
  } else {
    actualParty = party;
  }

  agents = actualParty?.agents || [];

  const primaryAgent = agents.find((a) => a.role === "primary") || agents[0];
  const associateAgents = primaryAgent
    ? agents.filter((a) => a.id !== primaryAgent.id)
    : agents.slice(1);

  const accentColor = partyRole === "claimant" ? "#00a884" : "#8e44ad";

  const partyName =
    actualParty?.name ||
    actualParty?.partyName ||
    (actualParty?.respondentKind ? actualParty.name : label);

  return (
    <>
      <PartyCardHeader>
        <PartyAccentBar color={accentColor} />
        <PartyHeaderContent>
          <Row gap="8px">
            <PartyHeaderLabel>{label}</PartyHeaderLabel>
            <Badge bg={badgeBg} color={badgeColor}>
              {agents.length ? agents.length : respondentParties?.length} agent
              {agents.length !== 1 ? "s" : ""}
            </Badge>
          </Row>

          {primaryAgent ? (
            <AgentInlineRow>
              <InitialsCircleSmall color={accentColor}>
                {getInitials(primaryAgent.user?.name || primaryAgent.name)}
              </InitialsCircleSmall>
              <AgentInlineInfo>
                <AgentInlineName>
                  {party?.name || primaryAgent.name || partyName || "—"}
                </AgentInlineName>
                <AgentInlineRole>Primary Agent</AgentInlineRole>
              </AgentInlineInfo>

              {associateAgents.length > 0 && (
                <AssocInlineGroup>
                  <AvatarStack>
                    {associateAgents.slice(0, 3).map((a, i) => (
                      <MiniAvatar
                        key={i}
                        $index={i}
                        color={AVATAR_COLORS[i % AVATAR_COLORS.length]}
                        title={a.user?.name || a.name}
                      >
                        {getInitials(a.user?.name || a.name)}
                      </MiniAvatar>
                    ))}
                    {associateAgents.length > 3 && (
                      <MiniAvatar $index={3} color="#6b7280">
                        +{associateAgents.length - 3}
                      </MiniAvatar>
                    )}
                  </AvatarStack>
                  <AssocInlineLabel>
                    {associateAgents.length} Assoc.
                  </AssocInlineLabel>
                </AssocInlineGroup>
              )}
            </AgentInlineRow>
          ) : (
            <AgentInlineRow line={false}>
              <InitialsCircleSmall color={accentColor}>
                {getInitials(partyName || partyName)}
              </InitialsCircleSmall>
              <AgentInlineInfo>
                <AgentInlineName>{partyName || "—"}</AgentInlineName>
              </AgentInlineInfo>
              {/* <NoAgentsInline>No agents added</NoAgentsInline> */}
            </AgentInlineRow>
          )}
        </PartyHeaderContent>
      </PartyCardHeader>

      <PartyCardBodySlim>
        <LinkText onClick={onViewAgents}>View agents</LinkText>
      </PartyCardBodySlim>
    </>
  );
};

const CaseSheetTab = ({
  caseObj,
  setOpenDrawer,
  items,
  resolutionKind,
  selectedIndex,
  isMediation,
  status,
  currentStateKey,
  setStatusModal,
  removeMediator,
  presidingArbitrator,
  addMediator,
  caseDetails,
  setOpenHearing,
  setTimelineModal,
  setFields,
  partyList,
  viewOnly,
  openTrackingModal,
  pushCaseToArbitrationAlert,
  openPayments,
  setRespondentModal,
  setRefreshDetails,
  currentState,
}) => {
  const [lifeCycleModal, setCaseLifeCyleModal] = useState(false);
  const [agentModal, setAgentModal] = useState(null);

  const getRespondent = () => {
    return (
      partyList?.find((p) => p.partyRole === "respondent") ||
      caseObj?.respondentParties?.[0] ||
      null
    );
  };

  const respondentParty = getRespondent();

  return (
    <>
      <Container>
        <TopRow>
          <TopCard $index={0}>
            <StageCardHeader>
              <PartyAccentBar color="#006970" />
              <PartyHeaderContent>
                <Row
                  gap="8px"
                  style={{ justifyContent: "space-between", flex: 1 }}
                >
                  <PartyHeaderLabel>Stage</PartyHeaderLabel>
                  <ScratchBtn>
                    <NextHearing>
                      <Calendar size={12} />
                      <HearingLink
                        clickable={caseObj?.hearingDatesSet?.length > 0}
                        onClick={() =>
                          caseObj?.hearingDatesSet?.length > 0 &&
                          !viewOnly &&
                          setTimelineModal({
                            state: true,
                            data: caseObj.hearingDatesSet,
                          })
                        }
                      >
                        Next:
                      </HearingLink>
                      <UpdateTime
                        onClick={() => {
                          if (viewOnly) return;

                          setOpenHearing((prev) => ({
                            ...prev,
                            state: true,
                          }));
                          setTimelineModal({ state: false });
                        }}
                      >
                        {caseObj?.nextHearingDate?.date?.length
                          ? caseObj?.nextHearingDate?.time
                          : "N/A"}
                      </UpdateTime>
                    </NextHearing>
                  </ScratchBtn>
                </Row>
                <StageHeaderTitle>
                  {currentState?.key === caseObj?.status
                    ? currentState?.text
                    : _.startCase(caseObj?.status) || "In Progress"}
                </StageHeaderTitle>

                <ProgressBars style={{ marginTop: "10px" }}>
                  {[0, 1, 2, 3].map((i) => (
                    <ProgressBarTrack key={i}>
                      <ProgressBarFill
                        active={i <= selectedIndex - 1}
                        $delay={i * 0.09}
                      />
                    </ProgressBarTrack>
                  ))}
                </ProgressBars>
              </PartyHeaderContent>
            </StageCardHeader>

            <PartyCardBodySlim>
              <LinkText onClick={() => setCaseLifeCyleModal(true)}>
                View lifecycle
              </LinkText>
            </PartyCardBodySlim>
          </TopCard>

          <TopCard $index={1}>
            <PartyContent
              label="Claimant"
              badgeBg="#FEF3E2"
              badgeColor="#A55A00"
              party={caseObj?.claimantParty}
              partyRole="claimant"
              onViewAgents={() =>
                setAgentModal({
                  partyRole: "claimant",
                  party: caseObj?.claimantParty || {},
                })
              }
            />
          </TopCard>

          <TopCard $index={2}>
            <PartyContent
              label="Respondent"
              badgeBg="#EEF0F7"
              badgeColor="#293462"
              party={respondentParty}
              respondentParties={caseObj?.respondentParties}
              partyRole="respondent"
              onViewAgents={() =>
                setAgentModal({
                  partyRole: "respondent",
                  party: respondentParty || {},
                })
              }
            />
          </TopCard>
        </TopRow>

        <TribunalWrapper $index={3}>
          <TribunalSection
            caseDetails={caseObj}
            {...{
              removeMediator,
              addMediator,
              viewOnly,
              pushCaseToArbitrationAlert,
              openPayments,
            }}
            makePresidingArbitrator={presidingArbitrator}
          />
        </TribunalWrapper>

        <TopCard $index={4} as="div">
          <StageCardHeader>
            <PartyAccentBar color="#006970" />
            <PartyHeaderContent>
              <Row
                gap="8px"
                style={{ justifyContent: "space-between", flex: 1 }}
              >
                <PartyHeaderLabel>Case Description</PartyHeaderLabel>
                <ScratchBtn onClick={() => setFields(true)}>
                  Case Fields
                </ScratchBtn>
              </Row>
            </PartyHeaderContent>
          </StageCardHeader>

          <DescriptionContent>
            {caseObj.description || "Commercial dispute under loan agreement."}
            {caseObj?.caseTopic?.length > 0 && (
              <ScratchBtn>Keywords: {caseObj.caseTopic.join(", ")}</ScratchBtn>
            )}
          </DescriptionContent>
        </TopCard>

        <RevealBlock $index={5}>
          <CaseStatusHistoryCard caseObj={caseObj} />
        </RevealBlock>

        {caseObj?.resolutionKind === "arbitration" &&
          caseObj?.trackingNumbers?.length > 0 && (
            <DispatchCard $index={6}>
              <DispatchLeft>
                <div>
                  <DispatchTitle>
                    Dispatches &amp; Tracking Covers
                  </DispatchTitle>
                  <MutedText>
                    Multiple dispatches • India Post EA-series
                  </MutedText>
                </div>
              </DispatchLeft>
              <ActionButton onClick={openTrackingModal}>
                <EyeIcon /> View Tracking Covers
              </ActionButton>
            </DispatchCard>
          )}
      </Container>

      {lifeCycleModal && (
        <LifecyclePopoverModal
          open={lifeCycleModal}
          onClose={() => setCaseLifeCyleModal(false)}
          {...{
            items,
            resolutionKind,
            selectedIndex,
            isMediation,
            status,
            currentStateKey,
            setStatusModal,
            caseObj,
            currentState,
          }}
        />
      )}

      {agentModal && (
        <AgentDetailsModal
          partyData={agentModal}
          onClose={() => setAgentModal(null)}
          caseDetails={caseObj}
          {...{ setRespondentModal, setRefreshDetails }}
        />
      )}
    </>
  );
};

export default CaseSheetTab;

const Container = styled.div`
  padding: 20px 0;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 768px) {
    padding: 14px;
    gap: 12px;
  }
`;

const TopRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  align-items: stretch;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const revealAnimation = css`
  opacity: 0;
  ${motionSafe(css`
    animation: ${fadeInUp} 0.55s ${EASE_OUT_EXPO} forwards;
    animation-delay: ${(props) => (props.$index || 0) * 0.07}s;
  `)}
`;

export const TopCard = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  ${revealAnimation}
  transition:
    box-shadow 0.35s ${EASE_OUT_EXPO},
    transform 0.35s ${EASE_OUT_EXPO},
    border-color 0.35s ${EASE_OUT_EXPO};

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 16px 32px rgba(15, 23, 42, 0.08);
    border-color: #d6dbe8;
  }
`;

const RevealBlock = styled.div`
  ${revealAnimation}
`;

const TribunalWrapper = styled.div`
  width: 100%;
  ${revealAnimation}
`;

const PartyCardHeader = styled.div`
  padding: 14px 18px;
  background: linear-gradient(155deg, #2f3a68 0%, #232c50 100%);
  display: flex;
  align-items: flex-start;
  gap: 10px;
  flex-shrink: 0;
`;

export const StageCardHeader = styled(PartyCardHeader)`
  padding: 14px 18px 14px;
`;

export const PartyAccentBar = styled.div`
  width: 4px;
  min-height: 32px;
  align-self: stretch;
  border-radius: 999px;
  background: ${(props) => props.color || "#00a884"};
  flex-shrink: 0;
`;

export const PartyHeaderContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

export const PartyHeaderLabel = styled.div`
  font-size: 11px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.65);
  line-height: 1;
`;

export const StageHeaderTitle = styled.div`
  font-size: 14px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: #ffffff;
  line-height: 1.3;
`;

const AgentInlineRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  flex-wrap: wrap;
`;

const InitialsCircleSmall = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: ${(props) => props.color || "#00a884"};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  flex-shrink: 0;
`;

const AgentInlineInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const AgentInlineName = styled.div`
  font-size: 12.5px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AgentInlineRole = styled.div`
  font-size: 10.5px;
  color: rgba(255, 255, 255, 0.55);
  margin-top: 1px;
`;

const AssocInlineGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
`;

const AssocInlineLabel = styled.div`
  font-size: 10.5px;
  color: rgba(255, 255, 255, 0.6);
  white-space: nowrap;
`;

const PartyCardBodySlim = styled.div`
  padding: 10px 18px;
  display: flex;
  align-items: center;
`;

const ProgressBars = styled.div`
  display: flex;
  gap: 6px;
`;

const ProgressBarTrack = styled.div`
  flex: 1;
  height: 5px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.2);
  overflow: hidden;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  width: 100%;
  border-radius: 999px;
  transform-origin: left center;
  transform: scaleX(${(props) => (props.active ? 1 : 0)});
  background: #00818a;
  ${motionSafe(css`
    animation: ${(props) => (props.active ? growIn : "none")} 0.6s
      ${EASE_OUT_EXPO} forwards;
    animation-delay: ${(props) => props.$delay || 0}s;
  `)}
`;

const AvatarStack = styled.div`
  display: flex;
  align-items: center;
`;

const MiniAvatar = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${(props) => props.color};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 700;
  margin-left: -5px;
  border: 1.5px solid #293461;
  flex-shrink: 0;
  transition: transform 0.25s ${EASE_SPRING};

  &:first-child {
    margin-left: 0;
  }

  ${AssocInlineGroup}:hover & {
    transform: translateX(${(props) => (props.$index || 0) * 2}px);
  }
`;

export const Row = styled.div`
  display: flex;
  align-items: center;
  gap: ${(props) => props.gap || "12px"};
  flex-wrap: wrap;
`;

export const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  line-height: 1;
  background: ${(props) => props.bg || "#E6F2F3"};
  color: ${(props) => props.color || "#006970"};
`;

const LinkArrow = styled.span`
  display: inline-block;
  transition: transform 0.25s ${EASE_OUT_EXPO};
`;

const LinkText = styled.div`
  font-size: 12px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: #006970;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 3px;

  &:hover {
    text-decoration: underline;
  }

  &:hover ${LinkArrow} {
    transform: translateX(3px);
  }
`;

const MutedText = styled.div`
  font-size: 12px;
  font-family: ${theme.fonts.primaryFontRegular};
  color: #6b7280;
  line-height: 1.5;
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  transition:
    box-shadow 0.35s ${EASE_OUT_EXPO},
    transform 0.35s ${EASE_OUT_EXPO};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.06);
  }
`;

const DescriptionContent = styled.div`
  padding: 18px 20px;
  font-size: 13px;
  font-family: ${theme.fonts.primaryFontRegular};
  line-height: 1.8;
  color: #293461;
`;

const DispatchCard = styled(Card)`
  padding: 18px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  ${revealAnimation}

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 14px 16px;
  }
`;

const DispatchLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const DispatchTitle = styled.div`
  font-size: 14px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: #111827;
  line-height: 1.4;
  margin-bottom: 4px;
`;

const ActionButton = styled.button`
  border: 1px solid #e2e8f0;
  background: white;
  color: #006970;
  padding: 9px 16px;
  border-radius: 10px;
  font-size: 13px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition:
    background 0.25s ${EASE_OUT_EXPO},
    border-color 0.25s ${EASE_OUT_EXPO},
    transform 0.2s ${EASE_SPRING};
  white-space: nowrap;

  &:hover {
    background: #e6f2f3;
    border-color: #00818a;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0) scale(0.98);
  }
`;

const EyeIcon = () => (
  <svg
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    viewBox="0 0 24 24"
  >
    <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

export { EASE_OUT_EXPO, EASE_SPRING, fadeInUp, scaleFadeIn, motionSafe };
